import { Component, Input, OnDestroy } from '@angular/core';

import { first, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiChapterSubjectCategoryListItem, ApiReplaceSubjectCategoryRequest, ApiSubjectCategory, ApiThema, ChaptersClient, SubjectCategoriesClient, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { TableSettings } from 'sc-common';

@Component({
    selector: 'cm-chapter-themas',
    templateUrl: './chapter-themas.component.html',
    standalone: false
})
export class ChapterThemasComponent implements OnDestroy {

    @Input({ required: true }) public isReadonly = false;

    public readonly tableSettings: TableSettings<ApiChapterSubjectCategoryListItem>;

    public readonly selectChapterHint = $localize`Select a chapter to edit its themas.`;

    public selectedChapter: ApiChapterSubjectCategoryListItem | null = null;

    public selectedThemaCodes: string[] = [];

    public requestInProcess = false;

    private _originalSelectedThemaCodes: string[] = [];

    private readonly _refreshTable$ = new Subject<void>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _subjectCategoriesClient: SubjectCategoriesClient,
        private readonly _chaptersClient: ChaptersClient) {

        this.tableSettings = this._initTable();
    }

    public get hasChanges(): boolean {

        if (this._originalSelectedThemaCodes.length !== this.selectedThemaCodes.length) {
            return true;
        }

        const sortedOriginal = [...this._originalSelectedThemaCodes].sort();
        const sortedCurrent = [...this.selectedThemaCodes].sort();

        return sortedOriginal.some((v, i) => v !== sortedCurrent[i]);
    }

    public get canUpdate(): boolean {
        return !this.isReadonly && !!this.selectedChapter && this.hasChanges && !this.requestInProcess;
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public onSelectionChange(chapter: ApiChapterSubjectCategoryListItem | null): void {

        this.selectedChapter = chapter;

        const codes = chapter?.themas?.map(t => t.code) ?? [];

        this.selectedThemaCodes = [...codes];
        this._originalSelectedThemaCodes = [...codes];
    }

    public update(): void {

        if (!this.selectedChapter) {
            return;
        }

        this.requestInProcess = true;

        const chapterId = this.selectedChapter.id;

        this._subjectCategoriesClient.getThemas().pipe(
            first(),
            switchMap(themas => this._projectStateService.firstSwitchMap(project =>
                this._chaptersClient.replaceSubjectCategories(
                    project.id,
                    chapterId,
                    new ApiReplaceSubjectCategoryRequest({
                        subjectCategoryType: SubjectCategoryType.Thema,
                        subjectCategories: this._buildSubjectCategories(themas)
                    })))),
            takeUntil(this._destroy$))
            .subscribe(() => {
                this.requestInProcess = false;
                this._originalSelectedThemaCodes = [...this.selectedThemaCodes];
                this._refreshTable$.next();
            });
    }

    private _buildSubjectCategories(themas: ApiThema[]): ApiSubjectCategory[] {
        return this.selectedThemaCodes
            .map(code => themas.find(t => t.code === code))
            .filter((t): t is ApiThema => !!t)
            .map(thema => new ApiSubjectCategory({
                subjectCategoryType: SubjectCategoryType.Thema,
                code: thema.code,
                headingText: thema.heading
            }));
    }

    private _initTable(): TableSettings<ApiChapterSubjectCategoryListItem> {
        return TableSettings.create(ApiChapterSubjectCategoryListItem, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project =>
                this._chaptersClient.getSubjectCategoryList(project.id, SubjectCategoryType.Thema, oDataQuery)),
            columnResizeMode: 'fit',
            paginator: false,
            selectionMode: this.isReadonly ? undefined : 'single',
            emptyMessageText: $localize`No chapters`,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                pos: {
                    width: '100px',
                    centered: true
                },
                themas: {
                    header: $localize`Themas`,
                    displayExpr: item => item.code,
                    filterExpr: item => item.code,
                    renderAsCommaSeparatedList: true
                }
            },
            hasFilters: false,
            defaultSort: {
                field: x => x.pos
            }
        });
    }
}
